import React, { useState } from "react";
import { Button, FormControl, TextField, Box, Typography, Paper, CircularProgress, Backdrop, Select, MenuItem, InputLabel } from '@mui/material';
import { useNavigate } from "react-router-dom";
import ApiConfig from '../../../utils/services/apiConfig'
import { useKeycloak } from '@react-keycloak/web';
import { useMountEffect } from '../../../utils/hooks';

const AUTH_TYPE_NONE = 'none';
const AUTH_TYPE_BASIC = 'basic';
const AUTH_TYPE_BEARER = 'bearer';

const initialLivestreamState = {
  Id: "",
  RobotTableId: "",
  CameraPosition: "",
  Url: "",
  AuthType: AUTH_TYPE_NONE,
  AuthUrl: "",
  BasicAuthUsername: "",
  BasicAuthPassword: "",
  BearerClientId: "",
  BearerClientSecret: "",
  UpdatedBy: "",
  CreatedBy: "",
};

const LoadingScreen = ({ open }) => (
    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
    </Backdrop>
);

function CreateRobotLivestream(props) {
  const [loading, setLoading] = useState(false);
  const [robots, setRobots] = useState([])
  const [livestream, setLivestream] = useState(initialLivestreamState);
  const { keycloak } = useKeycloak();
  const a = new ApiConfig(keycloak)
  const apiConfig = a.api()
  const navigate = useNavigate();

  const initRetrieve = () => {
    Promise.all([
        apiConfig.fetchRobotData(0),
    ]).then(results => {
        console.log(results[0])
        setRobots(results[0].RobotResponse)
    }).catch(error => {
        console.error(error);
    });
  }

  useMountEffect(() => {
    initRetrieve()
  }, []);

  const handleCreate = () => {
    // console.log(software)
    setLoading(true);
    Promise.all([
        apiConfig.createRobotLivestream(livestream),
    ]).then(results => {
        // setTimeout(() => {
        console.log(results[0])
        setLoading(false);
        navigate("/managements/robot_livestream_management");
        // }, 5000);
    }).catch(error => {
        console.error(error);
        alert(error)
        setLoading(false)
    });
  }

  const handleLivestreamChanges = (e) => {
    const { name, value } = e.target;
    const username = keycloak.tokenParsed?.preferred_username || "system";

    setLivestream(prevState => {
      const nextState = {
        ...prevState,
        [name]: value,
        UpdatedBy: username,
        CreatedBy: username,
      };

      if (name === 'AuthType') {
        if (value !== AUTH_TYPE_BASIC) {
          nextState.BasicAuthUsername = '';
          nextState.BasicAuthPassword = '';
        }

        if (value !== AUTH_TYPE_BEARER) {
          nextState.AuthUrl = '';
          nextState.BearerClientId = '';
          nextState.BearerClientSecret = '';
        }
      }

      return nextState;
    });
  };

  const handleCancel = () => {
    setLoading(false)
    setLivestream(initialLivestreamState)
    navigate("/managements/robot_livestream_management");
  }

  return (
    <Box p={2}>
    <LoadingScreen open={loading} />
      <Typography variant="h4">Create New Robot Camera</Typography>
      <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
        <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="robot-select-label">Robot</InputLabel>
            <Select
                labelId="robot-select-label"
                id="robot-select"
                label="Robot"
                name="RobotTableId"
                value={livestream.RobotTableId || ''}
                onChange={handleLivestreamChanges}
            >
              {robots.map((robot) => (
                <MenuItem key={robot.Id} value={robot.Id}>{robot.RobotId}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
              fullWidth
              label="Robot Camera Position"
              name="CameraPosition"
              variant="outlined"
              value={livestream.CameraPosition}
              InputLabelProps={{ shrink: true }}
              onChange={handleLivestreamChanges}
              sx={{ mb: 2 }}
          />
          <TextField
              fullWidth
              label="Camera Livestream URL"
              name="Url"
              variant="outlined"
              value={livestream.Url}
              InputLabelProps={{ shrink: true }}
              onChange={handleLivestreamChanges}
              sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="auth-type-label">Authentication Type</InputLabel>
            <Select
                labelId="auth-type-label"
                id="auth-type-select"
                label="Authentication Type"
                name="AuthType"
                value={livestream.AuthType || AUTH_TYPE_NONE}
                onChange={handleLivestreamChanges}
            >
              <MenuItem value={AUTH_TYPE_NONE}>No Auth</MenuItem>
              <MenuItem value={AUTH_TYPE_BASIC}>Basic Auth</MenuItem>
              <MenuItem value={AUTH_TYPE_BEARER}>Bearer Token</MenuItem>
            </Select>
          </FormControl>

          {livestream.AuthType === AUTH_TYPE_BASIC && (
            <>
              <TextField
                  fullWidth
                  label="Basic Auth Username"
                  name="BasicAuthUsername"
                  variant="outlined"
                  value={livestream.BasicAuthUsername || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleLivestreamChanges}
                  sx={{ mb: 2 }}
              />
              <TextField
                  fullWidth
                  label="Basic Auth Password"
                  name="BasicAuthPassword"
                  type="password"
                  variant="outlined"
                  value={livestream.BasicAuthPassword || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleLivestreamChanges}
                  sx={{ mb: 2 }}
              />
            </>
          )}

          {livestream.AuthType === AUTH_TYPE_BEARER && (
            <>
              <TextField
                  fullWidth
                  label="Auth URL"
                  name="AuthUrl"
                  variant="outlined"
                  value={livestream.AuthUrl || ''}
                  placeholder="Optional"
                  helperText="Optional when using platform OAuth authentication"
                  InputLabelProps={{ shrink: true }}
                  onChange={handleLivestreamChanges}
                  sx={{ mb: 2 }}
              />
              <TextField
                  fullWidth
                  label="Bearer Client ID"
                  name="BearerClientId"
                  variant="outlined"
                  value={livestream.BearerClientId || ''}
                  placeholder="Optional"
                  helperText="Optional when using platform OAuth authentication"
                  InputLabelProps={{ shrink: true }}
                  onChange={handleLivestreamChanges}
                  sx={{ mb: 2 }}
              />
              <TextField
                  fullWidth
                  label="Bearer Client Secret"
                  name="BearerClientSecret"
                  type="password"
                  variant="outlined"
                  value={livestream.BearerClientSecret || ''}
                  placeholder="Optional"
                  helperText="Optional when using platform OAuth authentication"
                  InputLabelProps={{ shrink: true }}
                  onChange={handleLivestreamChanges}
                  sx={{ mb: 2 }}
              />
            </>
          )}
          <Button variant="contained" color="primary" type="submit">
            Create
          </Button>
          <Button variant="contained" color="primary" type="button" onClick={handleCancel}>
            Cancel
          </Button>
        </form>
      </Paper>
    </Box>
  );
}

export default CreateRobotLivestream;
